import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GoogleAdsDashboard from '../modules/googleAds/GoogleAdsDashboard';
import { googleAuthService } from '../modules/googleAds/services/googleAuthService';
import { motion } from 'framer-motion';
import { Brain, AlertCircle, LogIn } from 'lucide-react';

const GoogleAdsPage: React.FC = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    try {
      setLoading(true);
      const authenticated = googleAuthService.isAuthenticated();
      setIsAuthenticated(authenticated);
      
      if (!authenticated) {
        console.log('👤 Użytkownik niezalogowany - pokazuję ekran logowania');
      }
    } catch (error) {
      console.error('Błąd sprawdzania autoryzacji:', error);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Brain className="w-16 h-16 text-purple-500 mx-auto mb-4 animate-pulse" />
          <p className="text-white/70">Sprawdzanie autoryzacji...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - show login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        {/* Animated background */}
        <div className="fixed inset-0 -z-10 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-purple-950/20 to-slate-950" />
          <div className="absolute top-0 -left-4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob" />
          <div className="absolute top-0 -right-4 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000" />
          <div className="absolute -bottom-8 left-20 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000" />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative w-full max-w-md"
        >
          <div className="rounded-3xl bg-white/5 backdrop-blur-2xl border border-white/10 p-8 shadow-2xl">
            <div className="flex justify-center mb-6">
              <div className="p-4 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 shadow-lg shadow-purple-500/30">
                <Brain className="w-12 h-12 text-white" />
              </div>
            </div>

            <h2 className="text-3xl font-bold text-center mb-2 bg-gradient-to-r from-white via-purple-200 to-pink-200 bg-clip-text text-transparent">
              Google Ads AI
            </h2>
            
            <p className="text-center text-white/60 mb-8">
              Zaloguj się aby zarządzać kampaniami Google Ads
            </p>

            <div className="space-y-4">
              <button
                onClick={() => {
                  // Inicjuj logowanie OAuth2
                  googleAuthService.initiateOAuth2Flow();
                }}
                className="w-full px-6 py-4 rounded-2xl font-medium bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40 transition-all duration-300 flex items-center justify-center gap-3"
              >
                <LogIn className="w-5 h-5" />
                Zaloguj się przez Google
              </button>

              <button
                onClick={() => navigate('/')}
                className="w-full px-6 py-3 rounded-2xl font-medium bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all duration-300 text-white/70"
              >
                Wróć do panelu głównego
              </button>
            </div>

            <div className="mt-8 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-300">Wymagane uprawnienia</p>
                  <p className="text-xs text-yellow-300/60 mt-1">
                    Do korzystania z modułu Google Ads wymagane jest połączenie z kontem Google Ads
                  </p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-white/30 mt-4">
            PlexiSystem × Google Ads API v2.0
          </p>
        </motion.div>

        <style jsx>{`
          @keyframes blob {
            0% { transform: translate(0px, 0px) scale(1); }
            33% { transform: translate(30px, -50px) scale(1.1); }
            66% { transform: translate(-20px, 20px) scale(0.9); }
            100% { transform: translate(0px, 0px) scale(1); }
          }
          
          .animate-blob {
            animation: blob 7s infinite;
          }
          
          .animation-delay-2000 {
            animation-delay: 2s;
          }
          
          .animation-delay-4000 {
            animation-delay: 4s;
          }
        `}</style>
      </div>
    );
  }

  // Authenticated - show dashboard
  return <GoogleAdsDashboard />;
};

export default GoogleAdsPage;
